import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Button,
  CSSProperties,
  Group,
  Paper,
  rem,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { randomId, useDebouncedCallback } from "@mantine/hooks";
import {
  IconGripVertical,
  IconMinus,
  IconPlus,
  IconSortAscending,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import dayjs from "dayjs";
import { partition, sortBy } from "lodash-es";
import { z } from "zod";

import {
  getGetBookingQueryKey,
  getGetBookingsQueryKey,
  useUpdateBookingProgram,
} from "@/shared/api/generated/booking";
import { useGetEmployees } from "@/shared/api/generated/employee";
import {
  GetBookingData,
  GetBookingResponse,
  GetBookingsResponse,
} from "@/shared/api/generated/schemas";
import { Can } from "@/shared/providers/casl-provider";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";

function useProgramFormSchema() {
  const { t } = useLingui();

  return z.object({
    guideId: z.string().nullable(),
    program: z.array(
      z
        .object({
          id: z.string(),
          start: z.string(),
          end: z.string(),
          comment: z.string().optional(),
        })
        .refine(
          (data) => {
            const start = dayjs(data.start, "HH:mm");
            const end = dayjs(data.end, "HH:mm");

            if (start.isValid() && end.isValid()) {
              return end.isAfter(start);
            }
            return true;
          },
          {
            message: t`Slutt må være etter start`,
            path: ["end"],
          },
        ),
    ),
  });
}

type ProgramFormInitial = z.input<ReturnType<typeof useProgramFormSchema>>;
type ProgramFormSubmit = z.output<ReturnType<typeof useProgramFormSchema>>;

export default function Program({ booking }: { booking: GetBookingData }) {
  const { t } = useLingui();

  const [initialProgram, initialProducts] = partition(
    booking.program,
    (b) => b.productID === null,
  );

  const debouncedSubmit = useDebouncedCallback(() => {
    form.onSubmit(handleSubmit)();
  }, 1000);

  const programFormSchema = useProgramFormSchema();

  const form = useForm<ProgramFormInitial, () => ProgramFormSubmit>({
    mode: "controlled",
    validate: zodResolver(programFormSchema),
    initialValues: {
      guideId: booking.guide?.id ?? null,
      program: sortBy(initialProgram, "index")?.map((programItem) => ({
        id: programItem.id,
        start: programItem.start
          ? dayjs(programItem.start).format("HH:mm")
          : "",
        end: programItem.end ? dayjs(programItem.end).format("HH:mm") : "",
        comment: programItem.comment ?? "",
      })) ?? [
        {
          id: randomId(),
          start: "",
          end: "",
          comment: "",
        },
      ],
    },
    onValuesChange: debouncedSubmit,
    clearInputErrorOnChange: false,
  });

  useBlocker({
    blockerFn: () =>
      window.confirm(
        t`Det er gjort endringer i skjemaet som ikke er lagret. Trykk "OK" for å forkaste endringene.`,
      ),
    condition: form.isDirty(),
  });

  const queryClient = useQueryClient();

  const { mutateAsync: updateProgram } = useUpdateBookingProgram({
    mutation: {
      onSuccess: (data) => {
        // Update entire booking itself
        queryClient.setQueryData<GetBookingResponse>(
          getGetBookingQueryKey(data.data.id),
          (prev) => ({
            ...prev,
            data: { ...prev?.data, ...data.data },
          }),
        );
        // Update this booking in the for GET /bookings
        queryClient.setQueriesData<GetBookingsResponse>(
          { queryKey: getGetBookingsQueryKey() },
          (old) => ({
            ...old,
            data: (old?.data ?? []).map((booking) =>
              booking.id === data.data.id ? data.data : booking,
            ),
          }),
        );
      },
    },
  });

  async function handleSubmit(values: ProgramFormSubmit) {
    await updateProgram({
      id: booking.id,
      data: {
        guideId: values.guideId,
        products: [
          ...initialProducts,
          ...values.program.map((programItem, index) => {
            let start: string | null = null;

            if (programItem.start) {
              const startTime = dayjs(programItem.start, "HH:mm");
              start = dayjs(booking.arrival)
                .hour(startTime.hour())
                .minute(startTime.minute())
                .toISOString();
            }

            let end: string | null = null;

            if (programItem.end) {
              const endTime = dayjs(programItem.end, "HH:mm");
              end = dayjs(booking.arrival)
                .hour(endTime.hour())
                .minute(endTime.minute())
                .toISOString();
            }

            return {
              id: undefined,
              start: start,
              end: end,
              comment: programItem.comment,
              index,
              price: 0,
              quantity: 0,
            };
          }),
        ],
        updatedAt: booking.updatedAt,
      },
    });
    form.resetDirty(values);
  }

  const program = form.getValues().program;

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (active && over && active.id !== over.id) {
      form.reorderListItem("program", {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        from: active.data.current?.sortable.index,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        to:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          over.data.current?.sortable.index ??
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          active.data.current?.sortable.index,
      });
    }
  }

  function addNewProgramItem() {
    form.insertListItem("program", {
      id: randomId(),
      start: "",
      end: "",
      comment: "",
    } satisfies ProgramFormInitial["program"][0]);
  }

  function sortProgramByStartTime() {
    const currentProgram = form.getValues().program;
    const sorted = sortBy(currentProgram, (item) => {
      if (!item.start) return Infinity;
      return dayjs(item.start, "HH:mm").valueOf();
    });
    form.setFieldValue("program", sorted);
  }

  const { data: employees } = useGetEmployees(
    {
      locations: [booking.location.id],
    },
    {
      query: { enabled: !!booking.location.id },
    },
  );

  const guideOptions = employees?.data?.map((employee) => ({
    value: employee.id,
    label: `${employee.firstName} ${employee.lastName ?? ""}`,
  }));

  const ability = useAppAbility();
  const canUpdateBooking = (field: GetFieldsBySubjectKind<"Booking">) =>
    ability.can("update", getSubject("Booking", booking), field);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <Paper p="xs" h="100%">
        <Text size="sm" mb="sm">
          <Trans>Program</Trans>
        </Text>
        <Stack gap="sm">
          <Group>
            <Select
              label={t`Guide`}
              placeholder={t`Velg guide`}
              data={sortBy(guideOptions, (o) => o.label.toLowerCase())}
              searchable
              clearable
              {...form.getInputProps("guideId")}
              key={form.key("guideId")}
              readOnly={!canUpdateBooking("guide.id")}
            />
          </Group>
          <ScrollArea>
            <Table
              withRowBorders={false}
              horizontalSpacing={4}
              verticalSpacing={4}
            >
              <Table.Thead>
                <Table.Tr>
                  <Can
                    I="update"
                    this={getSubject("Booking", booking)}
                    field="program"
                  >
                    <Table.Th style={{ width: rem(32) }} />
                  </Can>
                  <Table.Th style={{ width: rem(75) }}>
                    <Trans>Start</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: rem(75) }}>
                    <Trans>Slutt</Trans>
                  </Table.Th>
                  <Table.Th style={{ minWidth: rem(150) }}>
                    <Trans>Kommentar</Trans>
                  </Table.Th>
                  <Can
                    I="update"
                    this={getSubject("Booking", booking)}
                    field="program"
                  >
                    <Table.Th style={{ width: rem(36) }} />
                  </Can>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                <SortableContext
                  items={program.map(({ id }) => id)}
                  strategy={verticalListSortingStrategy}
                >
                  {program.map((programItem, index) => (
                    <DraggableRow
                      key={programItem.id}
                      id={programItem.id}
                      index={index}
                      form={form}
                      booking={booking}
                    />
                  ))}
                  {program.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Text c="dimmed" size="sm" ta="center" mt="sm">
                          <Trans>
                            Klikk på &quot;Legg til programlinje&quot; for å
                            legge til programlinjer i programmet
                          </Trans>
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </SortableContext>
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Can I="update" this={getSubject("Booking", booking)} field="program">
            <Group>
              <Button
                onClick={addNewProgramItem}
                leftSection={<IconPlus size={16} />}
                size="compact-xs"
                variant="outline"
                color="gray"
              >
                <Trans>Legg til programlinje</Trans>
              </Button>
              <Button
                onClick={sortProgramByStartTime}
                leftSection={<IconSortAscending size={16} />}
                size="compact-xs"
                variant="outline"
                color="gray"
              >
                <Trans>Sorter etter tid</Trans>
              </Button>
            </Group>
          </Can>
        </Stack>
      </Paper>
    </DndContext>
  );
}

function DraggableRow({
  id,
  index,
  form,
  booking,
}: {
  id: string;
  index: number;
  form: ReturnType<typeof useForm<ProgramFormInitial>>;
  booking: GetBookingData;
}) {
  const { transform, transition, setNodeRef, attributes, listeners } =
    useSortable({
      id,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  const ability = useAppAbility();

  const canUpdateBookingProgram = (field: GetFieldsBySubjectKind<"Booking">) =>
    ability.can("update", getSubject("Booking", booking), field);

  return (
    <Table.Tr ref={setNodeRef} style={style}>
      <Can
        I="update"
        this={getSubject("Booking", booking)}
        field={"program.index" satisfies GetFieldsBySubjectKind<"Booking">}
      >
        <Table.Td {...attributes} {...listeners} valign="top">
          <IconGripVertical style={{ marginTop: 6 }} />
        </Table.Td>
      </Can>

      <Table.Td valign="top">
        <TimeInput
          {...form.getInputProps(`program.${index}.start`)}
          key={form.key(`program.${index}.start`)}
          readOnly={!canUpdateBookingProgram("program.start")}
        />
      </Table.Td>
      <Table.Td valign="top">
        <TimeInput
          {...form.getInputProps(`program.${index}.end`)}
          key={form.key(`program.${index}.end`)}
          readOnly={!canUpdateBookingProgram("program.end")}
        />
      </Table.Td>
      <Table.Td valign="top">
        <Textarea
          {...form.getInputProps(`program.${index}.comment`)}
          key={form.key(`program.${index}.comment`)}
          readOnly={!canUpdateBookingProgram("program.comment")}
          autosize
          minRows={1}
        />
      </Table.Td>
      <Can
        I="update"
        this={getSubject("Booking", booking)}
        field={"program.index" satisfies GetFieldsBySubjectKind<"Booking">}
      >
        <Table.Td valign="top">
          <ActionIcon
            onClick={() => {
              form.removeListItem("program", index);
            }}
            variant="outline"
            color="red"
            mt={4}
          >
            <IconMinus />
          </ActionIcon>
        </Table.Td>
      </Can>
    </Table.Tr>
  );
}
